import { useState } from "react";
import { Copy, Check, Facebook, Linkedin, Mail, Twitter } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline share bar — site-themed, no external SDKs, opens platform-native
 * share dialogs in a new tab.
 */
export default function ShareBar({ url, title, description }) {
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || title);

  const buttons = [
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
      testid: "share-facebook",
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
      testid: "share-linkedin",
    },
    {
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      Icon: Twitter,
      testid: "share-twitter",
    },
    {
      label: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
      Icon: Mail,
      testid: "share-email",
    },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 md:gap-3 border border-slate-200 bg-white p-3 md:p-4"
      data-testid="share-bar"
    >
      <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 mr-1 md:mr-3">
        Share
      </span>
      {buttons.map(({ label, href, Icon, testid }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={testid}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-heading font-bold uppercase tracking-[0.12em] text-[#0A192F] border border-slate-300 hover:bg-[#0A192F] hover:text-white hover:border-[#0A192F] transition-colors"
          aria-label={`Share on ${label}`}
        >
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{label}</span>
        </a>
      ))}
      <button
        onClick={onCopy}
        data-testid="share-copy-link"
        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-heading font-bold uppercase tracking-[0.12em] text-[#0A192F] border border-slate-300 hover:bg-[#C9A349] hover:text-[#0A192F] hover:border-[#C9A349] transition-colors"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
      </button>
    </div>
  );
}
